import React, { forwardRef } from 'react';
import styles from './Progress.module.css';

/**
 * Progress — a shadcn/Radix-style determinate progress bar in Athena's
 * idiom (TSX + CSS Module + 2.0 tokens), hand-rolled (no Radix).
 *
 * Track: --muted, 4px, full radius. Indicator: --primary, positioned via
 * translateX(-(100 - value)%) so value changes animate on the compositor
 * (transform, not width).
 *
 * Reproduces Radix Progress's accessibility contract: role="progressbar",
 * aria-valuemin/max/now and data-state ("loading" | "complete" |
 * "indeterminate") / data-value / data-max. value is clamped to [0, max];
 * null/undefined = indeterminate (empty bar, no aria-valuenow).
 *
 * Distinct from TopProgressBar (the global indeterminate strip on its
 * token bus) and the AnalysisProgress composite, which are consumers'
 * concerns, not this primitive's.
 */

export interface ProgressProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role'> {
  /** Current value, 0..max. null/undefined = indeterminate. */
  value?: number | null;
  /** Maximum value (default 100). */
  max?: number;
}

const Progress = forwardRef<HTMLDivElement, ProgressProps>(
  ({ className = '', value = null, max = 100, ...props }, ref) => {
    const isNumber = typeof value === 'number' && !Number.isNaN(value);
    const clamped = isNumber ? Math.min(Math.max(value as number, 0), max) : null;
    const pct = clamped != null && max > 0 ? (clamped / max) * 100 : 0;
    const state =
      clamped == null ? 'indeterminate' : clamped >= max ? 'complete' : 'loading';

    return (
      <div
        ref={ref}
        data-slot="progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={clamped ?? undefined}
        data-state={state}
        data-value={clamped ?? undefined}
        data-max={max}
        className={`${styles.progress} ${className}`.trim()}
        {...props}
      >
        <div
          data-slot="progress-indicator"
          className={styles.indicator}
          style={{ transform: `translateX(-${100 - pct}%)` }}
        />
      </div>
    );
  },
);

Progress.displayName = 'Progress';

export default Progress;
export { Progress };
